import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * CONVERSATION FLOW EDITOR
 * Visual drag-and-drop editor for character conversations
 * Like ManyChat/Dialogflow - create branching dialogue trees
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Node Types
const NODE_TYPES = {
  START: 'start',
  MESSAGE: 'message',
  CHOICE: 'choice',
  ACTION: 'action',
  CONDITION: 'condition',
  END: 'end'
};

// Action Types for ACTION nodes
const ACTION_TYPES = {
  GIVE_GEMS: 'give_gems',
  TAKE_GEMS: 'take_gems',
  GIVE_ITEM: 'give_item',
  TAKE_ITEM: 'take_item',
  UNLOCK_WORD: 'unlock_word',
  CALL_TAXI: 'call_taxi',
  UNLOCK_LOCATION: 'unlock_location'
};

// Default node data by type
const getDefaultNodeData = (type) => {
  switch (type) {
    case NODE_TYPES.START:
      return { text: 'Conversation starts here' };
    case NODE_TYPES.MESSAGE:
      return { 
        text: 'Character says something...', 
        speaker: 'character',
        emotion: 'neutral',
        darija: '',
        translation: ''
      };
    case NODE_TYPES.CHOICE:
      return { 
        text: 'What do you say?',
        options: [
          { id: `opt_${Date.now()}_1`, text: 'Option 1', nextNodeId: null },
          { id: `opt_${Date.now()}_2`, text: 'Option 2', nextNodeId: null }
        ]
      };
    case NODE_TYPES.ACTION:
      return { 
        actionType: ACTION_TYPES.GIVE_GEMS,
        amount: 10,
        itemId: null,
        word: null,
        message: 'You received 10 gems!'
      };
    case NODE_TYPES.CONDITION:
      return {
        conditionType: 'has_item',
        itemId: null,
        minGems: null,
        trueNextId: null,
        falseNextId: null
      };
    case NODE_TYPES.END:
      return { text: 'Goodbye!', emotion: 'happy' };
    default:
      return {};
  }
};

// Node colors by type
const NODE_COLORS = {
  [NODE_TYPES.START]: { bg: '#4CAF50', border: '#2E7D32', icon: '▶️' },
  [NODE_TYPES.MESSAGE]: { bg: '#2196F3', border: '#1565C0', icon: '💬' },
  [NODE_TYPES.CHOICE]: { bg: '#FF9800', border: '#E65100', icon: '❓' },
  [NODE_TYPES.ACTION]: { bg: '#9C27B0', border: '#6A1B9A', icon: '🎁' },
  [NODE_TYPES.CONDITION]: { bg: '#00BCD4', border: '#00838F', icon: '🔀' },
  [NODE_TYPES.END]: { bg: '#F44336', border: '#C62828', icon: '🔚' }
};

// Generate unique ID
const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ============================================
// MAIN COMPONENT
// ============================================
const ConversationFlowEditor = ({ characters = [], locations = [], onSave }) => {
  // State for all conversation flows
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Canvas state
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Node editing state
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [draggingNodeId, setDraggingNodeId] = useState(null);
  const [dragNodeOffset, setDragNodeOffset] = useState({ x: 0, y: 0 });
  
  // Connection drawing state
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  // Load flows on mount
  useEffect(() => {
    loadFlows();
  }, []);
  
  const loadFlows = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-flows`);
      if (response.ok) {
        const data = await response.json();
        setFlows(data);
      }
    } catch (error) {
      console.error('Failed to load flows:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Get current flow
  const currentFlow = flows.find(f => f.id === selectedFlowId);
  
  // ============================================
  // FLOW MANAGEMENT
  // ============================================
  
  const createNewFlow = (characterId = null) => {
    const character = characters.find(c => c.id === characterId);
    const newFlow = {
      id: `flow_${Date.now()}`,
      character_id: characterId,
      character_name: character?.name || 'Unknown',
      location_id: null,
      name: `New Conversation ${flows.length + 1}`,
      nodes: [
        {
          id: 'start_node',
          type: NODE_TYPES.START,
          position: { x: 400, y: 50 },
          data: getDefaultNodeData(NODE_TYPES.START),
          nextNodeId: null
        }
      ],
      startNodeId: 'start_node',
      created_at: new Date().toISOString()
    };
    
    setFlows([...flows, newFlow]);
    setSelectedFlowId(newFlow.id);
    return newFlow;
  };
  
  const deleteFlow = async (flowId) => {
    if (!window.confirm('Delete this conversation flow?')) return;
    
    try {
      await fetch(`${API_URL}/api/conversation-flows/${flowId}`, {
        method: 'DELETE'
      });
      setFlows(flows.filter(f => f.id !== flowId));
      if (selectedFlowId === flowId) {
        setSelectedFlowId(null);
        setSelectedNodeId(null);
      }
    } catch (error) {
      console.error('Failed to delete flow:', error);
    }
  };
  
  const saveCurrentFlow = async () => {
    if (!currentFlow) return;
    
    try {
      const response = await fetch(`${API_URL}/api/conversation-flows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentFlow)
      });
      
      if (response.ok) {
        alert('✅ Conversation saved!');
        if (onSave) onSave(currentFlow);
      } else {
        alert('❌ Failed to save');
      }
    } catch (error) {
      console.error('Failed to save flow:', error);
      alert('❌ Error saving conversation');
    }
  };
  
  const updateFlow = (updates) => {
    setFlows(flows.map(f => 
      f.id === selectedFlowId ? { ...f, ...updates } : f
    ));
  };
  
  // ============================================
  // NODE MANAGEMENT
  // ============================================
  
  const addNode = (type, position = null) => {
    if (!currentFlow) return;
    
    const newNode = {
      id: generateId(),
      type,
      position: position || { 
        x: 400 + Math.random() * 100, 
        y: 150 + currentFlow.nodes.length * 120 
      },
      data: getDefaultNodeData(type),
      nextNodeId: null
    };
    
    updateFlow({
      nodes: [...currentFlow.nodes, newNode]
    });
    
    setSelectedNodeId(newNode.id);
    return newNode;
  };
  
  const updateNode = (nodeId, updates) => {
    if (!currentFlow) return;
    
    updateFlow({
      nodes: currentFlow.nodes.map(n =>
        n.id === nodeId ? { ...n, ...updates } : n
      )
    });
  };
  
  const updateNodeData = (nodeId, dataUpdates) => {
    if (!currentFlow) return;
    
    updateFlow({
      nodes: currentFlow.nodes.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...dataUpdates } } : n
      )
    });
  };
  
  const deleteNode = (nodeId) => {
    if (!currentFlow) return;
    if (nodeId === 'start_node') {
      alert('Cannot delete the start node!');
      return;
    }
    
    // Remove node and all connections to it
    const updatedNodes = currentFlow.nodes
      .filter(n => n.id !== nodeId)
      .map(n => {
        // Remove references to deleted node
        const updatedNode = { ...n };
        if (updatedNode.nextNodeId === nodeId) {
          updatedNode.nextNodeId = null;
        }
        if (updatedNode.data?.options) {
          updatedNode.data = {
            ...updatedNode.data,
            options: updatedNode.data.options.map(opt => ({
              ...opt,
              nextNodeId: opt.nextNodeId === nodeId ? null : opt.nextNodeId
            }))
          };
        }
        if (updatedNode.data?.trueNextId === nodeId) {
          updatedNode.data = { ...updatedNode.data, trueNextId: null };
        }
        if (updatedNode.data?.falseNextId === nodeId) {
          updatedNode.data = { ...updatedNode.data, falseNextId: null };
        }
        return updatedNode;
      });
    
    updateFlow({ nodes: updatedNodes });
    
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
    }
  };
  
  // ============================================
  // CONNECTION MANAGEMENT
  // ============================================
  
  const startConnection = (nodeId, outputId = null) => {
    setIsConnecting(true);
    setConnectionStart({ nodeId, outputId });
  };
  
  const completeConnection = (targetNodeId) => {
    if (!connectionStart || !currentFlow) return;
    
    const { nodeId: sourceNodeId, outputId } = connectionStart;
    
    // Don't connect to self
    if (sourceNodeId === targetNodeId) {
      setIsConnecting(false);
      setConnectionStart(null);
      return;
    }
    
    // Update the source node's connection
    updateFlow({
      nodes: currentFlow.nodes.map(n => {
        if (n.id !== sourceNodeId) return n;
        
        // If outputId is specified, it's a choice option
        if (outputId && n.data?.options) {
          return {
            ...n,
            data: {
              ...n.data,
              options: n.data.options.map(opt =>
                opt.id === outputId ? { ...opt, nextNodeId: targetNodeId } : opt
              )
            }
          };
        }
        
        // For condition nodes
        if (outputId === 'true' && n.type === NODE_TYPES.CONDITION) {
          return { ...n, data: { ...n.data, trueNextId: targetNodeId } };
        }
        if (outputId === 'false' && n.type === NODE_TYPES.CONDITION) {
          return { ...n, data: { ...n.data, falseNextId: targetNodeId } };
        }
        
        // Regular next connection
        return { ...n, nextNodeId: targetNodeId };
      })
    });
    
    setIsConnecting(false);
    setConnectionStart(null);
  };
  
  const cancelConnection = () => {
    setIsConnecting(false);
    setConnectionStart(null);
  };
  
  // ============================================
  // CANVAS INTERACTIONS
  // ============================================
  
  const handleCanvasMouseDown = (e) => {
    if (e.target === canvasRef.current) {
      // Click on empty canvas - start panning
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      setSelectedNodeId(null);
      
      if (isConnecting) {
        cancelConnection();
      }
    }
  };
  
  const handleCanvasMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePos({
        x: (e.clientX - rect.left - pan.x) / zoom,
        y: (e.clientY - rect.top - pan.y) / zoom
      });
    }
    
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
    
    if (draggingNodeId && currentFlow) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / zoom - dragNodeOffset.x;
      const y = (e.clientY - rect.top - pan.y) / zoom - dragNodeOffset.y;
      
      updateNode(draggingNodeId, { position: { x, y } });
    }
  };
  
  const handleCanvasMouseUp = () => {
    setIsDragging(false);
    setDraggingNodeId(null);
  };
  
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(z => Math.min(2, Math.max(0.3, z * delta)));
  };
  
  // ============================================
  // RENDER HELPERS
  // ============================================
  
  const getNodeConnections = () => {
    if (!currentFlow) return [];
    
    const connections = [];
    
    currentFlow.nodes.forEach(node => {
      // Regular next connection
      if (node.nextNodeId) {
        const targetNode = currentFlow.nodes.find(n => n.id === node.nextNodeId);
        if (targetNode) {
          connections.push({
            from: node,
            to: targetNode,
            fromOutput: null
          });
        }
      }
      
      // Choice options
      if (node.data?.options) {
        node.data.options.forEach((opt, idx) => {
          if (opt.nextNodeId) {
            const targetNode = currentFlow.nodes.find(n => n.id === opt.nextNodeId);
            if (targetNode) {
              connections.push({
                from: node,
                to: targetNode,
                fromOutput: opt.id,
                outputIndex: idx
              });
            }
          }
        });
      }
      
      // Condition branches
      if (node.type === NODE_TYPES.CONDITION) {
        if (node.data?.trueNextId) {
          const targetNode = currentFlow.nodes.find(n => n.id === node.data.trueNextId);
          if (targetNode) {
            connections.push({
              from: node,
              to: targetNode,
              fromOutput: 'true',
              color: '#4CAF50'
            });
          }
        }
        if (node.data?.falseNextId) {
          const targetNode = currentFlow.nodes.find(n => n.id === node.data.falseNextId);
          if (targetNode) {
            connections.push({
              from: node,
              to: targetNode,
              fromOutput: 'false',
              color: '#F44336'
            });
          }
        }
      }
    });
    
    return connections;
  };
  
  const selectedNode = currentFlow?.nodes.find(n => n.id === selectedNodeId);
  
  // ============================================
  // RENDER
  // ============================================
  
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading conversation flows...</div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>🎭 Conversation Flow Editor</h2>
        <p style={styles.subtitle}>Create visual conversation trees for your characters</p>
      </div>
      
      <div style={styles.mainLayout}>
        {/* Left Sidebar - Flow List */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <h3 style={styles.sidebarTitle}>Conversations</h3>
            <button 
              onClick={() => createNewFlow(characters[0]?.id)}
              style={styles.addButton}
            >
              + New
            </button>
          </div>
          
          <div style={styles.flowList}>
            {flows.length === 0 ? (
              <div style={styles.emptyState}>
                No conversations yet.<br/>
                Click "+ New" to create one.
              </div>
            ) : (
              flows.map(flow => (
                <div
                  key={flow.id}
                  onClick={() => {
                    setSelectedFlowId(flow.id);
                    setSelectedNodeId(null);
                  }}
                  style={{
                    ...styles.flowItem,
                    ...(selectedFlowId === flow.id ? styles.flowItemActive : {})
                  }}
                >
                  <div style={styles.flowItemName}>{flow.name}</div>
                  <div style={styles.flowItemMeta}>
                    👤 {flow.character_name || 'No character'}
                  </div>
                  <div style={styles.flowItemMeta}>
                    📍 {flow.location_id ? locations.find(l => l.id === flow.location_id)?.name || 'Unknown' : 'All locations'}
                  </div>
                  <div style={styles.flowItemNodes}>
                    {flow.nodes?.length || 0} nodes
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Main Canvas Area */}
        <div style={styles.canvasContainer}>
          {currentFlow ? (
            <>
              {/* Toolbar */}
              <div style={styles.toolbar}>
                <div style={styles.toolbarLeft}>
                  <input
                    type="text"
                    value={currentFlow.name}
                    onChange={(e) => updateFlow({ name: e.target.value })}
                    style={styles.flowNameInput}
                    placeholder="Conversation name..."
                  />
                  
                  <select
                    value={currentFlow.character_id || ''}
                    onChange={(e) => {
                      const char = characters.find(c => c.id === e.target.value);
                      updateFlow({ 
                        character_id: e.target.value || null,
                        character_name: char?.name || 'Unknown'
                      });
                    }}
                    style={styles.select}
                  >
                    <option value="">-- Select Character --</option>
                    {characters.map(char => (
                      <option key={char.id} value={char.id}>
                        {char.name}
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={currentFlow.location_id || ''}
                    onChange={(e) => updateFlow({ location_id: e.target.value || null })}
                    style={styles.select}
                  >
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div style={styles.toolbarRight}>
                  <button onClick={saveCurrentFlow} style={styles.saveButton}>
                    💾 Save
                  </button>
                  <button 
                    onClick={() => deleteFlow(currentFlow.id)} 
                    style={styles.deleteButton}
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              {/* Node Palette */}
              <div style={styles.nodePalette}>
                <span style={styles.paletteLabel}>Add Node:</span>
                {Object.entries(NODE_TYPES).filter(([key]) => key !== 'START').map(([key, type]) => (
                  <button
                    key={type}
                    onClick={() => addNode(type)}
                    style={{
                      ...styles.paletteButton,
                      backgroundColor: NODE_COLORS[type]?.bg || '#666'
                    }}
                    title={`Add ${key.toLowerCase()} node`}
                  >
                    {NODE_COLORS[type]?.icon} {key}
                  </button>
                ))}
              </div>
              
              {/* Canvas */}
              <div
                ref={canvasRef}
                style={styles.canvas}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                onWheel={handleWheel}
              >
                {/* SVG for connections */}
                <svg style={styles.connectionsSvg}>
                  <defs>
                    <marker
                      id="arrowhead"
                      markerWidth="10"
                      markerHeight="7"
                      refX="9"
                      refY="3.5"
                      orient="auto"
                    >
                      <polygon points="0 0, 10 3.5, 0 7" fill="#FFD700" />
                    </marker>
                  </defs>
                  
                  <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
                    {/* Existing connections */}
                    {getNodeConnections().map((conn, idx) => {
                      const fromX = conn.from.position.x + 200;
                      const fromY = conn.from.position.y + 40 + (conn.outputIndex || 0) * 25;
                      const toX = conn.to.position.x;
                      const toY = conn.to.position.y + 40;
                      
                      const midX = (fromX + toX) / 2;
                      
                      return (
                        <path
                          key={idx}
                          d={`M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`}
                          stroke={conn.color || '#FFD700'}
                          strokeWidth="3"
                          fill="none"
                          markerEnd="url(#arrowhead)"
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                        />
                      );
                    })}
                    
                    {/* Drawing connection */}
                    {isConnecting && connectionStart && (
                      <line
                        x1={currentFlow.nodes.find(n => n.id === connectionStart.nodeId)?.position.x + 140 || 0}
                        y1={currentFlow.nodes.find(n => n.id === connectionStart.nodeId)?.position.y + 40 || 0}
                        x2={mousePos.x}
                        y2={mousePos.y}
                        stroke="#2196F3"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                      />
                    )}
                  </g>
                </svg>
                
                {/* Nodes */}
                <div 
                  style={{
                    ...styles.nodesContainer,
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`
                  }}
                >
                  {currentFlow.nodes.map(node => (
                    <FlowNode
                      key={node.id}
                      node={node}
                      isSelected={selectedNodeId === node.id}
                      isConnecting={isConnecting}
                      onSelect={() => setSelectedNodeId(node.id)}
                      onStartDrag={(offset) => {
                        setDraggingNodeId(node.id);
                        setDragNodeOffset(offset);
                      }}
                      onStartConnection={(outputId) => startConnection(node.id, outputId)}
                      onCompleteConnection={() => completeConnection(node.id)}
                      onDelete={() => deleteNode(node.id)}
                    />
                  ))}
                </div>
              </div>
              
              {/* Zoom controls */}
              <div style={styles.zoomControls}>
                <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} style={styles.zoomButton}>+</button>
                <span style={styles.zoomLevel}>{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} style={styles.zoomButton}>-</button>
                <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={styles.zoomButton}>⌂</button>
              </div>
            </>
          ) : (
            <div style={styles.noFlowSelected}>
              <div style={styles.noFlowIcon}>🎭</div>
              <h3>No Conversation Selected</h3>
              <p>Select a conversation from the left panel or create a new one</p>
              <button 
                onClick={() => createNewFlow(characters[0]?.id)}
                style={styles.createButton}
              >
                + Create New Conversation
              </button>
            </div>
          )}
        </div>
        
        {/* Right Panel - Node Properties */}
        {selectedNode && (
          <div style={styles.propertiesPanel}>
            <div style={styles.propertiesHeader}>
              <span style={{
                ...styles.nodeTypeIcon,
                backgroundColor: NODE_COLORS[selectedNode.type]?.bg
              }}>
                {NODE_COLORS[selectedNode.type]?.icon}
              </span>
              <h3 style={styles.propertiesTitle}>
                {selectedNode.type.toUpperCase()} Node
              </h3>
            </div>
            
            <NodePropertiesEditor
              node={selectedNode}
              characters={characters}
              locations={locations}
              onUpdate={(updates) => updateNodeData(selectedNode.id, updates)}
              allNodes={currentFlow?.nodes || []}
            />
            
            <div style={styles.propertiesFooter}>
              {selectedNode.type !== NODE_TYPES.START && (
                <button
                  onClick={() => deleteNode(selectedNode.id)}
                  style={styles.deleteNodeButton}
                >
                  🗑️ Delete Node
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// FLOW NODE COMPONENT
// ============================================
const FlowNode = ({ 
  node, 
  isSelected, 
  isConnecting,
  onSelect, 
  onStartDrag, 
  onStartConnection,
  onCompleteConnection,
  onDelete 
}) => {
  const colors = NODE_COLORS[node.type] || { bg: '#666', border: '#444', icon: '?' };
  
  const handleMouseDown = (e) => {
    e.stopPropagation();
    onSelect();
    
    const rect = e.currentTarget.getBoundingClientRect();
    onStartDrag({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  
  const handleOutputClick = (e, outputId = null) => {
    e.stopPropagation();
    if (!isConnecting) {
      onStartConnection(outputId);
    }
  };
  
  const handleNodeClick = (e) => {
    e.stopPropagation();
    if (isConnecting) {
      onCompleteConnection();
    } else {
      onSelect();
    }
  };
  
  const getNodePreview = () => {
    switch (node.type) {
      case NODE_TYPES.START:
        return '▶️ Start';
      case NODE_TYPES.MESSAGE:
        return node.data?.text?.substring(0, 30) + (node.data?.text?.length > 30 ? '...' : '');
      case NODE_TYPES.CHOICE:
        return `${node.data?.options?.length || 0} options`;
      case NODE_TYPES.ACTION:
        return node.data?.actionType?.replace('_', ' ');
      case NODE_TYPES.CONDITION:
        return node.data?.conditionType?.replace('_', ' ');
      case NODE_TYPES.END:
        return '🔚 End';
      default:
        return 'Node';
    }
  };
  
  return (
    <div
      style={{
        ...styles.node,
        left: node.position.x,
        top: node.position.y,
        backgroundColor: colors.bg,
        borderColor: isSelected ? '#FFD700' : colors.border,
        boxShadow: isSelected ? '0 0 0 3px rgba(255, 215, 0, 0.5)' : '0 4px 12px rgba(0,0,0,0.15)',
        cursor: isConnecting ? 'crosshair' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onClick={handleNodeClick}
    >
      {/* Header */}
      <div style={styles.nodeHeader}>
        <span style={styles.nodeIcon}>{colors.icon}</span>
        <span style={styles.nodeType}>{node.type}</span>
      </div>
      
      {/* Content */}
      <div style={styles.nodeContent}>
        {getNodePreview()}
      </div>
      
      {/* Input connector */}
      {node.type !== NODE_TYPES.START && (
        <div 
          style={styles.inputConnector}
          onClick={(e) => {
            e.stopPropagation();
            if (isConnecting) {
              onCompleteConnection();
            }
          }}
        />
      )}
      
      {/* Output connector(s) */}
      {node.type !== NODE_TYPES.END && (
        <>
          {node.type === NODE_TYPES.CHOICE ? (
            // Multiple outputs for choices
            <div style={styles.outputsContainer}>
              {node.data?.options?.map((opt, idx) => (
                <div
                  key={opt.id}
                  style={{
                    ...styles.outputConnector,
                    top: 40 + idx * 25
                  }}
                  onClick={(e) => handleOutputClick(e, opt.id)}
                  title={opt.text}
                />
              ))}
            </div>
          ) : node.type === NODE_TYPES.CONDITION ? (
            // Two outputs for condition
            <>
              <div
                style={{
                  ...styles.outputConnector,
                  backgroundColor: '#4CAF50',
                  top: 30
                }}
                onClick={(e) => handleOutputClick(e, 'true')}
                title="True"
              />
              <div
                style={{
                  ...styles.outputConnector,
                  backgroundColor: '#F44336',
                  top: 55
                }}
                onClick={(e) => handleOutputClick(e, 'false')}
                title="False"
              />
            </>
          ) : (
            // Single output
            <div
              style={styles.outputConnector}
              onClick={(e) => handleOutputClick(e, null)}
            />
          )}
        </>
      )}
    </div>
  );
};

// ============================================
// NODE PROPERTIES EDITOR
// ============================================
const NodePropertiesEditor = ({ node, characters, locations, onUpdate, allNodes }) => {
  switch (node.type) {
    case NODE_TYPES.START:
      return (
        <div style={styles.propertiesContent}>
          <p style={styles.helpText}>
            This is the starting point of the conversation.
            Connect it to the first message node.
          </p>
        </div>
      );
      
    case NODE_TYPES.MESSAGE:
      return (
        <div style={styles.propertiesContent}>
          <label style={styles.propLabel}>Character says:</label>
          <textarea
            value={node.data?.text || ''}
            onChange={(e) => onUpdate({ text: e.target.value })}
            style={styles.textarea}
            placeholder="What does the character say?"
            rows={3}
          />
          
          <label style={styles.propLabel}>Darija (optional):</label>
          <input
            type="text"
            value={node.data?.darija || ''}
            onChange={(e) => onUpdate({ darija: e.target.value })}
            style={styles.input}
            placeholder="Arabic/Darija text"
          />
          
          <label style={styles.propLabel}>Translation (optional):</label>
          <input
            type="text"
            value={node.data?.translation || ''}
            onChange={(e) => onUpdate({ translation: e.target.value })}
            style={styles.input}
            placeholder="English translation"
          />
          
          <label style={styles.propLabel}>Emotion:</label>
          <select
            value={node.data?.emotion || 'neutral'}
            onChange={(e) => onUpdate({ emotion: e.target.value })}
            style={styles.select}
          >
            <option value="neutral">😐 Neutral</option>
            <option value="happy">😊 Happy</option>
            <option value="sad">😢 Sad</option>
            <option value="angry">😠 Angry</option>
            <option value="surprised">😲 Surprised</option>
            <option value="thinking">🤔 Thinking</option>
            <option value="excited">🤩 Excited</option>
          </select>
        </div>
      );
      
    case NODE_TYPES.CHOICE:
      return (
        <div style={styles.propertiesContent}>
          <label style={styles.propLabel}>Prompt text:</label>
          <input
            type="text"
            value={node.data?.text || ''}
            onChange={(e) => onUpdate({ text: e.target.value })}
            style={styles.input}
            placeholder="What do you say?"
          />
          
          <label style={styles.propLabel}>Options:</label>
          {node.data?.options?.map((opt, idx) => (
            <div key={opt.id} style={styles.optionRow}>
              <span style={styles.optionNumber}>{idx + 1}</span>
              <input
                type="text"
                value={opt.text}
                onChange={(e) => {
                  const newOptions = [...node.data.options];
                  newOptions[idx] = { ...opt, text: e.target.value };
                  onUpdate({ options: newOptions });
                }}
                style={styles.optionInput}
                placeholder={`Option ${idx + 1}`}
              />
              <button
                onClick={() => {
                  const newOptions = node.data.options.filter((_, i) => i !== idx);
                  onUpdate({ options: newOptions });
                }}
                style={styles.removeOptionButton}
              >
                ×
              </button>
            </div>
          ))}
          
          <button
            onClick={() => {
              const newOptions = [
                ...(node.data?.options || []),
                { id: `opt_${Date.now()}`, text: `Option ${(node.data?.options?.length || 0) + 1}`, nextNodeId: null }
              ];
              onUpdate({ options: newOptions });
            }}
            style={styles.addOptionButton}
          >
            + Add Option
          </button>
        </div>
      );
      
    case NODE_TYPES.ACTION:
      return (
        <div style={styles.propertiesContent}>
          <label style={styles.propLabel}>Action Type:</label>
          <select
            value={node.data?.actionType || ACTION_TYPES.GIVE_GEMS}
            onChange={(e) => onUpdate({ actionType: e.target.value })}
            style={styles.select}
          >
            <option value={ACTION_TYPES.GIVE_GEMS}>💰 Give Gems</option>
            <option value={ACTION_TYPES.TAKE_GEMS}>💸 Take Gems</option>
            <option value={ACTION_TYPES.GIVE_ITEM}>🎁 Give Item</option>
            <option value={ACTION_TYPES.TAKE_ITEM}>📦 Take Item</option>
            <option value={ACTION_TYPES.UNLOCK_WORD}>📚 Unlock Darija Word</option>
            <option value={ACTION_TYPES.CALL_TAXI}>🚕 Call Taxi</option>
            <option value={ACTION_TYPES.UNLOCK_LOCATION}>🗺️ Unlock Location</option>
          </select>
          
          {(node.data?.actionType === ACTION_TYPES.GIVE_GEMS || 
            node.data?.actionType === ACTION_TYPES.TAKE_GEMS) && (
            <>
              <label style={styles.propLabel}>Amount:</label>
              <input
                type="number"
                value={node.data?.amount || 0}
                onChange={(e) => onUpdate({ amount: parseInt(e.target.value) || 0 })}
                style={styles.input}
                min="0"
              />
            </>
          )}
          
          {(node.data?.actionType === ACTION_TYPES.GIVE_ITEM ||
            node.data?.actionType === ACTION_TYPES.TAKE_ITEM) && (
            <>
              <label style={styles.propLabel}>Item ID:</label>
              <input
                type="text"
                value={node.data?.itemId || ''}
                onChange={(e) => onUpdate({ itemId: e.target.value })}
                style={styles.input}
                placeholder="item_orange, item_tea, etc."
              />
            </>
          )}
          
          {node.data?.actionType === ACTION_TYPES.UNLOCK_WORD && (
            <>
              <label style={styles.propLabel}>Darija Word:</label>
              <input
                type="text"
                value={node.data?.word || ''}
                onChange={(e) => onUpdate({ word: e.target.value })}
                style={styles.input}
                placeholder="e.g., Salam = Hello"
              />
            </>
          )}
          
          <label style={styles.propLabel}>Display Message:</label>
          <input
            type="text"
            value={node.data?.message || ''}
            onChange={(e) => onUpdate({ message: e.target.value })}
            style={styles.input}
            placeholder="You received 10 gems!"
          />
        </div>
      );
      
    case NODE_TYPES.CONDITION:
      return (
        <div style={styles.propertiesContent}>
          <label style={styles.propLabel}>Condition Type:</label>
          <select
            value={node.data?.conditionType || 'has_item'}
            onChange={(e) => onUpdate({ conditionType: e.target.value })}
            style={styles.select}
          >
            <option value="has_item">Has Item</option>
            <option value="has_gems">Has Gems (min)</option>
            <option value="visited_location">Visited Location</option>
            <option value="has_word">Knows Darija Word</option>
          </select>
          
          {node.data?.conditionType === 'has_item' && (
            <>
              <label style={styles.propLabel}>Item ID:</label>
              <input
                type="text"
                value={node.data?.itemId || ''}
                onChange={(e) => onUpdate({ itemId: e.target.value })}
                style={styles.input}
                placeholder="item_orange"
              />
            </>
          )}
          
          {node.data?.conditionType === 'has_gems' && (
            <>
              <label style={styles.propLabel}>Minimum Gems:</label>
              <input
                type="number"
                value={node.data?.minGems || 0}
                onChange={(e) => onUpdate({ minGems: parseInt(e.target.value) || 0 })}
                style={styles.input}
                min="0"
              />
            </>
          )}
          
          <p style={styles.helpText}>
            <span style={{ color: '#4CAF50' }}>●</span> True → condition is met<br/>
            <span style={{ color: '#F44336' }}>●</span> False → condition is not met
          </p>
        </div>
      );
      
    case NODE_TYPES.END:
      return (
        <div style={styles.propertiesContent}>
          <label style={styles.propLabel}>End Message:</label>
          <input
            type="text"
            value={node.data?.text || ''}
            onChange={(e) => onUpdate({ text: e.target.value })}
            style={styles.input}
            placeholder="Goodbye!"
          />
          
          <label style={styles.propLabel}>Emotion:</label>
          <select
            value={node.data?.emotion || 'happy'}
            onChange={(e) => onUpdate({ emotion: e.target.value })}
            style={styles.select}
          >
            <option value="happy">😊 Happy</option>
            <option value="neutral">😐 Neutral</option>
            <option value="sad">😢 Sad</option>
          </select>
        </div>
      );
      
    default:
      return null;
  }
};

// ============================================
// STYLES
// ============================================
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    minHeight: '700px',
    backgroundColor: '#1a1a2e'
  },
  header: {
    padding: '15px 20px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #0f3460',
    color: 'white'
  },
  title: {
    margin: 0,
    fontSize: '22px',
    color: '#e94560'
  },
  subtitle: {
    margin: '5px 0 0 0',
    color: '#a0a0a0',
    fontSize: '13px'
  },
  mainLayout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: '600px'
  },
  sidebar: {
    width: '260px',
    minWidth: '260px',
    backgroundColor: '#16213e',
    borderRight: '1px solid #0f3460',
    display: 'flex',
    flexDirection: 'column'
  },
  sidebarHeader: {
    padding: '15px',
    borderBottom: '1px solid #0f3460',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  sidebarTitle: {
    margin: 0,
    fontSize: '16px',
    color: 'white'
  },
  addButton: {
    padding: '8px 16px',
    backgroundColor: '#e94560',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },
  flowList: {
    flex: 1,
    overflowY: 'auto',
    padding: '10px'
  },
  flowItem: {
    padding: '12px',
    marginBottom: '8px',
    backgroundColor: '#1a1a2e',
    borderRadius: '8px',
    cursor: 'pointer',
    border: '2px solid #0f3460',
    transition: 'all 0.2s'
  },
  flowItemActive: {
    backgroundColor: '#0f3460',
    borderColor: '#e94560'
  },
  flowItemName: {
    fontWeight: 'bold',
    marginBottom: '4px',
    color: 'white'
  },
  flowItemMeta: {
    fontSize: '12px',
    color: '#a0a0a0'
  },
  flowItemNodes: {
    fontSize: '11px',
    color: '#666',
    marginTop: '4px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '30px',
    color: '#666'
  },
  canvasContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    minWidth: '500px'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 15px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #0f3460',
    gap: '10px'
  },
  toolbarLeft: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  toolbarRight: {
    display: 'flex',
    gap: '10px'
  },
  flowNameInput: {
    padding: '8px 12px',
    border: '1px solid #0f3460',
    borderRadius: '6px',
    fontSize: '14px',
    width: '200px',
    backgroundColor: '#1a1a2e',
    color: 'white'
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #0f3460',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: '#1a1a2e',
    color: 'white'
  },
  saveButton: {
    padding: '8px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  deleteButton: {
    padding: '8px 12px',
    backgroundColor: '#e94560',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  nodePalette: {
    display: 'flex',
    gap: '8px',
    padding: '10px 15px',
    backgroundColor: '#0f3460',
    borderBottom: '1px solid #16213e',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  paletteLabel: {
    fontWeight: 'bold',
    color: '#e94560',
    marginRight: '10px'
  },
  paletteButton: {
    padding: '8px 14px',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },
  canvas: {
    flex: 1,
    minHeight: '500px',
    backgroundColor: '#1a1a2e',
    backgroundImage: 'radial-gradient(#0f3460 1px, transparent 1px)',
    backgroundSize: '25px 25px',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'grab'
  },
  connectionsSvg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none'
  },
  nodesContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    transformOrigin: '0 0'
  },
  node: {
    position: 'absolute',
    width: '200px',
    minHeight: '80px',
    borderRadius: '12px',
    border: '3px solid',
    color: 'white',
    boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
    userSelect: 'none'
  },
  nodeHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  nodeIcon: {
    fontSize: '18px'
  },
  nodeType: {
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    opacity: 0.9
  },
  nodeContent: {
    padding: '12px 14px',
    fontSize: '14px',
    opacity: 0.95
  },
  inputConnector: {
    position: 'absolute',
    left: '-10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    backgroundColor: '#e94560',
    border: '3px solid white',
    borderRadius: '50%',
    cursor: 'crosshair',
    transition: 'all 0.2s'
  },
  outputConnector: {
    position: 'absolute',
    right: '-10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    backgroundColor: '#FFD700',
    border: '3px solid white',
    borderRadius: '50%',
    cursor: 'crosshair',
    transition: 'all 0.2s'
  },
  outputsContainer: {
    position: 'absolute',
    right: '-10px',
    top: 0
  },
  zoomControls: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#16213e',
    padding: '10px 15px',
    borderRadius: '10px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    border: '1px solid #0f3460'
  },
  zoomButton: {
    width: '32px',
    height: '32px',
    border: '1px solid #0f3460',
    borderRadius: '6px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s'
  },
  zoomLevel: {
    margin: '0 8px',
    fontSize: '13px',
    color: '#a0a0a0',
    fontWeight: 'bold'
  },
  noFlowSelected: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#a0a0a0',
    backgroundColor: '#1a1a2e'
  },
  noFlowIcon: {
    fontSize: '80px',
    marginBottom: '20px'
  },
  createButton: {
    marginTop: '20px',
    padding: '14px 28px',
    backgroundColor: '#e94560',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },
  propertiesPanel: {
    width: '340px',
    minWidth: '340px',
    backgroundColor: '#16213e',
    borderLeft: '1px solid #0f3460',
    display: 'flex',
    flexDirection: 'column'
  },
  propertiesHeader: {
    padding: '15px',
    borderBottom: '1px solid #0f3460',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: '#0f3460'
  },
  nodeTypeIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px'
  },
  propertiesTitle: {
    margin: 0,
    fontSize: '16px',
    color: 'white'
  },
  propertiesContent: {
    flex: 1,
    padding: '15px',
    overflowY: 'auto'
  },
  propertiesFooter: {
    padding: '15px',
    borderTop: '1px solid #0f3460'
  },
  propLabel: {
    display: 'block',
    marginBottom: '6px',
    marginTop: '15px',
    fontWeight: 'bold',
    fontSize: '13px',
    color: '#e94560'
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#1a1a2e',
    color: 'white'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical',
    boxSizing: 'border-box',
    backgroundColor: '#1a1a2e',
    color: 'white',
    minHeight: '80px'
  },
  helpText: {
    fontSize: '12px',
    color: '#a0a0a0',
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#0f3460',
    borderRadius: '8px',
    lineHeight: '1.5'
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px'
  },
  optionNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#FF9800',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  optionInput: {
    flex: 1,
    padding: '10px',
    border: '1px solid #0f3460',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: '#1a1a2e',
    color: 'white'
  },
  removeOptionButton: {
    width: '30px',
    height: '30px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: '#e94560',
    color: 'white',
    cursor: 'pointer',
    fontSize: '18px',
    flexShrink: 0,
    transition: 'all 0.2s'
  },
  addOptionButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#0f3460',
    border: '2px dashed #e94560',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#e94560',
    marginTop: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },
  deleteNodeButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#e94560',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },
  loading: {
    padding: '50px',
    textAlign: 'center',
    color: '#a0a0a0',
    backgroundColor: '#1a1a2e',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default ConversationFlowEditor;
